import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { PaywallModal } from "@/components/PaywallModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

export function ConversationDetail() {
  const { convId } = useParams();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const bottomRef = useRef(null);

  const { data: messages, isLoading, refetch } = useQuery({
    queryKey: ["conv-messages", convId],
    queryFn: async () => (await api.get(`/conversations/${convId}/messages`)).data,
    refetchInterval: 5000,
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (user?.role === "professional" && !isPro) { setPaywall(true); return; }
    setSending(true);
    try {
      await api.post(`/conversations/${convId}/messages`, { text: text.trim() });
      setText("");
      refetch();
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const list = Array.isArray(messages) ? messages : messages?.messages || [];

  return (
    <div className="flex flex-col mx-auto w-full max-w-2xl h-[calc(100vh-3.5rem-5.5rem)] sm:h-[calc(100vh-4rem-2.5rem)]" data-testid="conversation-page">
      <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-3">
        {isLoading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-2/3 rounded-2xl" />)}</div>
        ) : list.length ? (
          list.map((m, i) => {
            const mine = m.sender_id === user?.id || m.mine === true;
            return (
              <div key={m.id || i} data-testid={`message-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                  mine ? "bg-volt text-white rounded-l-2xl rounded-tr-2xl" : "bg-slate-100 text-slate-900 rounded-r-2xl rounded-tl-2xl"
                }`}>
                  {m.text}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-slate-500 text-center pt-8" data-testid="no-messages">Nessun messaggio. Scrivi per primo!</p>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="px-4 py-3 bg-white/80 backdrop-blur-xl border-t border-slate-100 flex gap-2" data-testid="message-form">
        <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
          className="h-12 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-full flex-1" placeholder="Scrivi un messaggio..." />
        <Button data-testid="send-message-btn" type="submit" disabled={sending || !text.trim()} size="icon"
          className="h-12 w-12 rounded-full bg-volt hover:bg-volt/90 text-white shrink-0 active:scale-95 transition-transform">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </Button>
      </form>
      <PaywallModal open={paywall} onClose={() => setPaywall(false)} reason="La chat con i clienti è una funzione Pro." />
    </div>
  );
}
